import { LoginBody, useRequestPasswordReset } from 'api/modules/authentication'
import cx from 'classnames'
import { screenSizes } from 'constants/global'
import { useIntl } from 'locales/index'
import React from 'react'
import { useMediaQuery } from 'react-responsive'
import { Link, RouteComponentProps } from 'react-router-dom'
import { RegisterForm } from 'shared/components/common/RegisterForm'
import { Alert, Button, Form } from '@commutifi-fe/ui'
import { useCommutifiErrorMessages } from '@commutifi-fe/commutifi-specific/hooks'
import { PublicRouting } from 'src/routing'
import { AuthLayout } from 'views/Anonymous/views/Auth/layout/AuthLayout'

interface RequestResetPasswordProps extends RouteComponentProps {
  /**
   * Defines the local URL we host the request reset password component
   */
  authUrl: string
}

const commonAlertProps = {
  style: { borderRadius: '4px' },
  className: 'u-margin-bottom--large'
}

export function RequestResetPassword({
  authUrl = `..${PublicRouting.auth.path}`,
  location
}: RequestResetPasswordProps) {
  const intl = useIntl()
  const { error, isPending: isLoading, isSuccess, mutate: requestPasswordReset } = useRequestPasswordReset()
  const { translateCommutifiError } = useCommutifiErrorMessages()
  const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })

  const handleSubmit = (values: Pick<LoginBody, 'email'>) => requestPasswordReset(values)

  return (
    <AuthLayout
      title={intl.formatMessage({ id: 'auth.passwordReset.title' })}
      subTitle={intl.formatMessage({ id: 'auth.passwordReset.desc' })}
      message={
        <>
          {error ? <Alert {...commonAlertProps} type="error" message={translateCommutifiError(error)} /> : null}

          {isSuccess ? (
            <Alert
              {...commonAlertProps}
              type="success"
              message={intl.formatMessage({ id: 'auth.passwordReset.success' })}
            />
          ) : null}
        </>
      }
    >
      <Form layout="vertical" requiredMark={false} onFinish={handleSubmit}>
        <RegisterForm.EmailField />
        <div className="u-flex-justify-end u-flex-align-start u-flex-wrap">
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue, getFieldsError }) => (
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block={isXSmall}
                className="u-margin-bottom--medium-2"
                disabled={
                  !getFieldValue('email') || Boolean(getFieldsError().filter(({ errors }) => errors.length).length)
                }
              >
                {intl.formatMessage({ id: 'auth.passwordReset.submit' })}
              </Button>
            )}
          </Form.Item>
          <Button ghost block={isXSmall} className={cx(!isXSmall && 'u-margin-left--medium-2')}>
            <Link
              // Keep the location state to make sure if a resume pathname is defined, we will be able
              // to redirect the user back to the appropriate page
              to={{
                pathname: authUrl,
                state: location.state
              }}
            >
              {intl.formatMessage({ id: 'auth.passwordReset.back' })}
            </Link>
          </Button>
        </div>
      </Form>
    </AuthLayout>
  )
}
